/**
 * MASTER 02 — EDITORIAL_STORY (Редакционная история).
 *
 * Long-read editorial spread: photo + editorial text.
 * Reuses HeroMaster tokens (palette, background) and CropModal.
 * Variants: A (photo left / text right), B (text left / photo right).
 */
import { useState } from "react";
import { toast } from "sonner";
import axios from "axios";
import { Input } from "@/components/ui/input";
import { Upload, Image as ImageIcon, Crop, Plus, ArrowUp, ArrowDown, Trash2 } from "lucide-react";
import CropModal from "@/components/editorial/CropModal";
import { HERO_PALETTES, HERO_BACKGROUNDS } from "@/components/editorial/HeroMaster";

const API_ROOT = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
const authHeaders = () => {
  const t = localStorage.getItem("anokina_token");
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const paletteByKey = (k) => HERO_PALETTES.find((p) => p.key === k) || HERO_PALETTES[2]; // olive
const backgroundByKey = (k) => HERO_BACKGROUNDS.find((b) => b.key === k) || HERO_BACKGROUNDS[0];

async function uploadDirect(file) {
  const fd = new FormData();
  fd.append("file", file);
  const res = await axios.post(`${API_ROOT}/api/admin/media/upload-file`, fd, {
    headers: { ...authHeaders(), "Content-Type": "multipart/form-data" },
  });
  return res.data;
}

// ---------- Media library picker (compact reuse) ----------
function MediaPicker({ open, onClose, onPick }) {
  const [items, setItems] = useState([]);
  const [loaded, setLoaded] = useState(false);
  if (open && !loaded) {
    axios.get(`${API_ROOT}/api/admin/media`, { headers: authHeaders() })
      .then((r) => setItems((r.data || []).filter((m) => (m.mime || "").startsWith("image/"))))
      .catch(() => {})
      .finally(() => setLoaded(true));
  }
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[55] bg-black/50 flex items-center justify-center px-4 py-6"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      data-testid="story-media-picker">
      <div className="w-full max-w-3xl max-h-[80vh] bg-cream rounded-2xl overflow-hidden flex flex-col">
        <div className="p-4 border-b border-espresso/10 flex items-center justify-between">
          <div className="font-serif text-lg text-espresso">Медиатека</div>
          <button className="text-sm opacity-70" onClick={onClose}>Закрыть</button>
        </div>
        <div className="p-4 overflow-y-auto grid grid-cols-2 md:grid-cols-4 gap-3">
          {items.map((m) => {
            const src = (m.file_url || "").startsWith("http") ? m.file_url : `${API_ROOT}${m.file_url}`;
            return (
              <button key={m.id} onClick={() => onPick(m)}
                className="text-left" data-testid={`story-media-item-${m.id}`}>
                <div className="aspect-square rounded overflow-hidden bg-neutral-100">
                  <img src={src} alt="" className="w-full h-full object-cover" />
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ---------- ImageSlot (same UX as HERO) ----------
function ImageSlot({ label, url, focalX, focalY, zoom, onFile, onClear, onOpenLibrary, onOpenCrop, testid }) {
  const [busy, setBusy] = useState(false);
  const handleFile = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setBusy(true);
    try { const m = await uploadDirect(f); onFile(m); toast.success("Фото загружено"); }
    catch (err) { toast.error("Не удалось: " + (err?.response?.data?.detail || err.message)); }
    finally { setBusy(false); e.target.value = ""; }
  };
  return (
    <div className="space-y-2" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-widest opacity-70">{label}</div>
      {url ? (
        <>
          <div className="relative w-full aspect-[4/5] rounded-lg overflow-hidden bg-neutral-100" data-testid={`${testid}-preview`}>
            <img src={url.startsWith("http") ? url : `${API_ROOT}${url}`} alt=""
              className="w-full h-full object-cover pointer-events-none"
              style={{
                objectPosition: `${(focalX ?? 0.5) * 100}% ${(focalY ?? 0.5) * 100}%`,
                transform: `scale(${zoom || 1})`,
                transformOrigin: `${(focalX ?? 0.5) * 100}% ${(focalY ?? 0.5) * 100}%`,
              }} />
          </div>
          <div className="flex flex-wrap gap-2">
            <label className="cursor-pointer inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-espresso/25 text-[12px]">
              <input type="file" accept="image/*" className="hidden" onChange={handleFile} data-testid={`${testid}-file`} />
              <Upload className="w-3.5 h-3.5" /> {busy ? "Загрузка…" : "Заменить"}
            </label>
            <button onClick={onOpenLibrary} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-espresso/25 text-[12px]" data-testid={`${testid}-library`}>
              <ImageIcon className="w-3.5 h-3.5" /> Из Медиатеки
            </button>
            <button onClick={onOpenCrop} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-espresso/25 text-[12px]" data-testid={`${testid}-crop`}>
              <Crop className="w-3.5 h-3.5" /> Настроить кадр
            </button>
            {onClear && <button onClick={onClear} className="text-[12px] opacity-60" data-testid={`${testid}-clear`}>Убрать</button>}
          </div>
        </>
      ) : (
        <div className="rounded-lg bg-cream/80 border border-dashed border-espresso/25 aspect-[4/5] flex items-center justify-center">
          <label className="cursor-pointer inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-espresso text-cream text-[12px]">
            <input type="file" accept="image/*" className="hidden" onChange={handleFile} data-testid={`${testid}-file`} />
            <Upload className="w-3.5 h-3.5" /> {busy ? "Загрузка…" : "Загрузить с устройства"}
          </label>
        </div>
      )}
    </div>
  );
}

// ============================================================
//   EDITOR
// ============================================================
export function EditorialStoryEditor({ block, onPatch }) {
  const c = block.content || {};
  const s = block.settings || {};
  const paragraphs = (c.extra && Array.isArray(c.extra.paragraphs)) ? c.extra.paragraphs : [];
  const [libraryFor, setLibraryFor] = useState(null);
  const [cropFor, setCropFor] = useState(null);

  const patchContent = (patch) => onPatch({ content: { ...c, ...patch } });
  const patchSettings = (patch) => onPatch({ settings: { ...s, ...patch } });
  const setParagraphs = (p) => patchContent({ extra: { ...(c.extra || {}), paragraphs: p } });

  const addPar = () => setParagraphs([...paragraphs, ""]);
  const setPar = (i, v) => { const a = paragraphs.slice(); a[i] = v; setParagraphs(a); };
  const delPar = (i) => setParagraphs(paragraphs.filter((_, k) => k !== i));
  const movePar = (i, d) => {
    const j = i + d; if (j < 0 || j >= paragraphs.length) return;
    const a = paragraphs.slice(); [a[i], a[j]] = [a[j], a[i]]; setParagraphs(a);
  };

  const variant = s.variant || "A";
  const saveCrop = ({ focal_x, focal_y, zoom }) => {
    if (cropFor === "desktop") patchSettings({ focal_x, focal_y, hero_zoom: zoom });
    else patchSettings({ mobile_focal_x: focal_x, mobile_focal_y: focal_y, hero_mobile_zoom: zoom });
    setCropFor(null);
  };
  const acceptMedia = (m, slot) => {
    if (slot === "desktop") patchContent({ hero_media_id: m.id, hero_url: m.file_url });
    else patchContent({ hero_mobile_media_id: m.id, hero_mobile_url: m.file_url });
    setLibraryFor(null);
  };

  return (
    <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] gap-4" data-testid="story-editor">
      {/* CONTENT */}
      <div className="rounded-2xl bg-white border hairline p-4 space-y-4">
        <div className="text-[10px] uppercase tracking-widest opacity-70">Содержание</div>
        <Input value={c.eyebrow || ""} onChange={(e) => patchContent({ eyebrow: e.target.value })}
          placeholder="Надзаголовок" data-testid="story-field-eyebrow" />
        <Input value={c.headline || ""} onChange={(e) => patchContent({ headline: e.target.value })}
          placeholder="Заголовок" className="font-serif text-lg" data-testid="story-field-headline" />
        <Input value={c.subtitle || ""} onChange={(e) => patchContent({ subtitle: e.target.value })}
          placeholder="Подзаголовок" data-testid="story-field-subtitle" />
        <textarea value={c.lead || ""} rows={2}
          onChange={(e) => patchContent({ lead: e.target.value })}
          className="w-full border rounded px-2 py-1.5 text-sm italic"
          placeholder="Вступление / лид"
          data-testid="story-field-lead" />

        <div className="pt-2">
          <div className="text-[10px] uppercase tracking-widest opacity-70 mb-2">Текстовые блоки</div>
          {paragraphs.map((p, i) => (
            <div key={i} className="border rounded p-2 mb-2" data-testid={`story-paragraph-${i}`}>
              <textarea value={p} rows={3}
                onChange={(e) => setPar(i, e.target.value)}
                className="w-full text-sm resize-y outline-none"
                placeholder={`Абзац ${i + 1}`} />
              <div className="flex items-center gap-1 mt-1 text-xs opacity-70">
                <button onClick={() => movePar(i, -1)} data-testid={`story-par-up-${i}`}><ArrowUp className="w-3.5 h-3.5" /></button>
                <button onClick={() => movePar(i, +1)} data-testid={`story-par-down-${i}`}><ArrowDown className="w-3.5 h-3.5" /></button>
                <button onClick={() => delPar(i)} className="text-red-600 ml-auto" data-testid={`story-par-del-${i}`}><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
            </div>
          ))}
          <button onClick={addPar} className="w-full py-2 rounded border border-dashed border-espresso/25 text-sm text-espresso"
            data-testid="story-add-paragraph">
            <Plus className="w-4 h-4 inline mr-1" /> Добавить блок
          </button>
        </div>

        <div className="pt-2">
          <label className="block text-[10px] uppercase tracking-widest opacity-70 mb-1">Акцентная фраза</label>
          <textarea value={c.quote || ""} rows={3}
            onChange={(e) => patchContent({ quote: e.target.value })}
            className="w-full border rounded px-2 py-1.5 text-sm font-serif italic"
            placeholder="Выделенная редакционная фраза"
            data-testid="story-field-quote" />
        </div>
        <Input value={c.caption || ""} onChange={(e) => patchContent({ caption: e.target.value })}
          placeholder="Подпись к фото" data-testid="story-field-caption" />
        <Input value={c.alt || ""} onChange={(e) => patchContent({ alt: e.target.value })}
          placeholder="Alt-текст" data-testid="story-field-alt" />
      </div>

      {/* IMAGES + APPEARANCE */}
      <div className="space-y-4">
        <div className="rounded-2xl bg-white border hairline p-4 space-y-3">
          <div className="text-[10px] uppercase tracking-widest opacity-70">Изображения</div>
          <ImageSlot label="Основное фото · desktop" url={c.hero_url}
            focalX={s.focal_x} focalY={s.focal_y} zoom={s.hero_zoom}
            onFile={(m) => patchContent({ hero_media_id: m.id, hero_url: m.file_url })}
            onClear={() => patchContent({ hero_media_id: null, hero_url: null })}
            onOpenLibrary={() => setLibraryFor("desktop")}
            onOpenCrop={() => setCropFor("desktop")}
            testid="story-image-desktop" />
          <ImageSlot label="Фото для mobile · необязательно" url={c.hero_mobile_url}
            focalX={s.mobile_focal_x} focalY={s.mobile_focal_y} zoom={s.hero_mobile_zoom}
            onFile={(m) => patchContent({ hero_mobile_media_id: m.id, hero_mobile_url: m.file_url })}
            onClear={() => patchContent({ hero_mobile_media_id: null, hero_mobile_url: null })}
            onOpenLibrary={() => setLibraryFor("mobile")}
            onOpenCrop={() => setCropFor("mobile")}
            testid="story-image-mobile" />
        </div>

        <div className="rounded-2xl bg-white border hairline p-4 space-y-4">
          <div className="text-[10px] uppercase tracking-widest opacity-70">Оформление</div>
          <div>
            <div className="text-[11px] opacity-70 mb-2">Вариант композиции</div>
            <div className="grid grid-cols-2 gap-2">
              {[
                { key: "A", label: "Фото слева / текст справа" },
                { key: "B", label: "Текст слева / фото справа" },
              ].map((v) => (
                <button key={v.key} onClick={() => patchSettings({ variant: v.key })}
                  className={`p-3 rounded-xl border text-left ${variant === v.key ? "border-espresso bg-espresso/5" : "border-espresso/15"}`}
                  data-testid={`story-variant-${v.key}`}>
                  <div className="text-[10px] uppercase tracking-widest opacity-70">Вариант {v.key}</div>
                  <div className="text-[11px] mt-1">{v.label}</div>
                </button>
              ))}
            </div>
          </div>
          <div>
            <div className="text-[11px] opacity-70 mb-2">Акцентный цвет</div>
            <div className="flex flex-wrap gap-2">
              {HERO_PALETTES.map((p) => (
                <button key={p.key} onClick={() => patchSettings({ palette: p.key })}
                  className={`w-7 h-7 rounded-full border-2 ${s.palette === p.key ? "border-espresso" : "border-white shadow"}`}
                  style={{ background: p.swatch }} title={p.label}
                  data-testid={`story-palette-${p.key}`} />
              ))}
            </div>
          </div>
          <div>
            <label className="block text-[11px] opacity-70 mb-1">Фон</label>
            <select value={s.background || "warm-ivory"}
              onChange={(e) => patchSettings({ background: e.target.value })}
              className="w-full h-10 rounded-md border border-espresso/25 bg-white px-3 text-sm outline-none"
              data-testid="story-background">
              {HERO_BACKGROUNDS.filter(b => b.key !== "full-image").map((b) => (
                <option key={b.key} value={b.key}>{b.label}</option>
              ))}
            </select>
          </div>
          <label className="flex items-center gap-3 text-sm">
            <input type="checkbox" checked={!!s.botanical}
              onChange={(e) => patchSettings({ botanical: e.target.checked })}
              data-testid="story-botanical" />
            <span>Ботаническая декоративная деталь</span>
          </label>
        </div>
      </div>

      <MediaPicker open={!!libraryFor} onClose={() => setLibraryFor(null)}
        onPick={(m) => acceptMedia(m, libraryFor)} />
      <CropModal open={!!cropFor}
        mode={cropFor || "desktop"}
        url={cropFor === "mobile" ? (c.hero_mobile_url || c.hero_url) : c.hero_url}
        focalX={cropFor === "mobile" ? (s.mobile_focal_x ?? 0.5) : (s.focal_x ?? 0.5)}
        focalY={cropFor === "mobile" ? (s.mobile_focal_y ?? 0.5) : (s.focal_y ?? 0.5)}
        zoom={cropFor === "mobile" ? (s.hero_mobile_zoom || 1) : (s.hero_zoom || 1)}
        onSave={saveCrop} onClose={() => setCropFor(null)} />
    </div>
  );
}

// ============================================================
//   RENDER — reader-facing composition
// ============================================================
export function EditorialStoryRender({ block, mode = "desktop" }) {
  const c = block.content || {};
  const s = block.settings || {};
  const variant = s.variant || "A";
  const palette = paletteByKey(s.palette);
  const background = backgroundByKey(s.background);
  const paragraphs = (c.extra?.paragraphs || []).filter(Boolean);
  const bg = background.css === "transparent" ? "#EFE6D5" : background.css;
  const textOnDark = background.key === "deep-cocoa";
  const primary = textOnDark ? "#F1E4CB" : "#3B241A";

  const desktopSrc = (c.hero_url || "").startsWith("http") ? c.hero_url : `${API_ROOT}${c.hero_url || ""}`;
  const mobileSrc = c.hero_mobile_url
    ? ((c.hero_mobile_url || "").startsWith("http") ? c.hero_mobile_url : `${API_ROOT}${c.hero_mobile_url}`)
    : desktopSrc;

  const fx = (s.focal_x ?? 0.5) * 100, fy = (s.focal_y ?? 0.5) * 100;
  const mfx = (s.mobile_focal_x ?? s.focal_x ?? 0.5) * 100;
  const mfy = (s.mobile_focal_y ?? s.focal_y ?? 0.5) * 100;
  const dz = s.hero_zoom || 1, mz = s.hero_mobile_zoom || 1;

  // ---------- MOBILE ----------
  if (mode === "mobile") {
    return (
      <section className="w-full px-6 py-10" style={{ background: bg, color: primary }}
               data-testid="story-render-mobile" data-variant={variant}>
        {c.eyebrow && (
          <div className="text-[10px] tracking-[0.42em] uppercase mb-3 flex items-center gap-2"
               style={{ color: palette.swatch }}>
            <span className="h-px w-6" style={{ background: palette.swatch }} />
            {c.eyebrow}
          </div>
        )}
        <h1 className="font-serif leading-[0.98]"
            style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "clamp(38px, 11vw, 52px)" }}>
          {c.headline || ""}
        </h1>
        {c.subtitle && <div className="italic text-[16px] mt-3 opacity-85 leading-snug">{c.subtitle}</div>}
        {c.lead && <p className="mt-6 text-[15px] italic leading-[1.75] opacity-90">{c.lead}</p>}
        {mobileSrc && (
          <div className="relative w-full aspect-[3/4] mt-6 overflow-hidden rounded-sm">
            <img src={mobileSrc} alt={c.alt || ""} className="w-full h-full object-cover"
              style={{ objectPosition: `${mfx}% ${mfy}%`, transform: `scale(${mz})`, transformOrigin: `${mfx}% ${mfy}%` }} />
          </div>
        )}
        {paragraphs.length > 0 && (
          <div className="mt-6 space-y-4 text-[15px] leading-[1.85]">
            {paragraphs.map((p, i) => <p key={i}>{p}</p>)}
          </div>
        )}
        {c.quote && (
          <blockquote className="mt-8 pl-4 border-l-2 font-serif italic text-[20px] leading-[1.4]"
                      style={{ borderColor: palette.swatch, color: primary }}>
            {c.quote}
          </blockquote>
        )}
        {c.caption && <div className="mt-6 text-[11px] italic opacity-60">{c.caption}</div>}
      </section>
    );
  }

  // ---------- DESKTOP ----------
  const ImgCol = (
    <div className="relative overflow-hidden" style={{ minHeight: 560 }}>
      {desktopSrc && (
        <img src={desktopSrc} alt={c.alt || ""} className="absolute inset-0 w-full h-full object-cover"
          style={{ objectPosition: `${fx}% ${fy}%`, transform: `scale(${dz})`, transformOrigin: `${fx}% ${fy}%` }} />
      )}
      {c.caption && (
        <div className="absolute bottom-4 left-4 text-[10px] italic px-2 py-1 rounded"
             style={{ background: "rgba(255,255,255,0.75)", color: "#3B241A" }}>{c.caption}</div>
      )}
    </div>
  );
  const TextCol = (
    <div className="px-10 lg:px-14 xl:px-16 py-14 lg:py-20" style={{ color: primary }}>
      {c.eyebrow && (
        <div className="text-[11px] tracking-[0.48em] uppercase mb-6 flex items-center gap-3"
             style={{ color: palette.swatch }}>
          <span className="h-px w-8" style={{ background: palette.swatch }} />
          {c.eyebrow}
        </div>
      )}
      <h1 className="font-serif leading-[0.92]"
          style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "clamp(52px, 5.5vw, 96px)" }}
          data-testid="story-render-headline">
        {c.headline || ""}
      </h1>
      {c.subtitle && <div className="italic text-[20px] mt-5 opacity-85 leading-tight max-w-lg">{c.subtitle}</div>}
      <div className="mt-6 flex items-center gap-2">
        <span className="h-px w-10" style={{ background: palette.swatch }} />
        <span className="h-1.5 w-1.5 rounded-full" style={{ background: palette.swatch }} />
      </div>
      {c.lead && (
        <p className="mt-8 font-serif italic text-[24px] leading-[1.35] max-w-lg">
          {c.lead}
        </p>
      )}
      {paragraphs.length > 0 && (
        <div className="mt-8 space-y-5 text-[16px] leading-[1.9] max-w-[540px]">
          {paragraphs.map((p, i) => <p key={i}>{p}</p>)}
        </div>
      )}
      {c.quote && (
        <blockquote className="mt-10 pl-6 border-l-2 max-w-lg font-serif"
                    style={{ borderColor: palette.swatch }}>
          <div className="text-[26px] leading-[1.3] italic" style={{ color: primary }}>{c.quote}</div>
        </blockquote>
      )}
    </div>
  );

  return (
    <section className="grid grid-cols-1 lg:grid-cols-[45%_55%]"
             style={{ background: bg, minHeight: 640 }}
             data-testid="story-render-desktop" data-variant={variant}>
      {variant === "A" ? (<>{ImgCol}{TextCol}</>) : (<>{TextCol}{ImgCol}</>)}
    </section>
  );
}
